import { Box, Button, Typography } from '@mui/material';
import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import {
	resetTickets,
	setActiveHistoryItem,
	setHistoryMode,
	setReportAnIssueMode,
} from 'store/ticket-service/ticketSlice';
import { ITicketChange } from 'store/ticket-service/types';
import { getUserById } from 'store/user-service/asyncActions';
import { getLoadedUsers } from 'store/user-service/selector';

import { NamespacesList } from 'core/enums/common';

import { ReactComponent as Warning } from 'assets/icons/warning-blue.svg';
import { titleTextColor } from 'assets/styles/_palette';

import { HistoryItem } from './HistoryItem';

interface IHistoryListProps {
	ticketHistory: Array<ITicketChange>;
	isHistoryLoading: boolean;
}

export const HistoryChangesList: FC<IHistoryListProps> = ({ ticketHistory, isHistoryLoading }) => {
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);
	const { t: ticketHistoryTranslate } = useTranslation(NamespacesList['changesVersioning']);

	const loadedUsers = useSelector(getLoadedUsers);

	const dispatch = useAppDispatch();

	const handleHistoryModeClose = () => {
		dispatch(setHistoryMode(false));
		dispatch(setActiveHistoryItem(null));
		dispatch(resetTickets());
	};

	const handleOpenReportAnIssue = () => {
		dispatch(setReportAnIssueMode(true));
		dispatch(setHistoryMode(false));
		dispatch(setActiveHistoryItem(null));
	};

	useEffect(() => {
		const uniqueUserIds = [
			...new Set(
				ticketHistory.map((item) => JSON.parse(item?.userEmail || '{}')?.id).filter((item) => item)
			),
		];
		const loadedIds = loadedUsers.map((user) => user.id);

		if (uniqueUserIds.length)
			uniqueUserIds.forEach((userId) => {
				if (!loadedIds.includes(userId)) dispatch(getUserById({ userId }));
			});
	}, [ticketHistory, loadedUsers]);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '16px',
				height: '100%',
				padding: '12px 20px',
				backgroundColor: '#FBFBFB',
				boxSizing: 'border-box',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
				}}
			>
				<Typography
					sx={{
						fontSize: '16px',
						fontWeight: '700',
						color: titleTextColor,
					}}
				>
					{ticketHistoryTranslate('text.historyOfChanges')}{' '}
				</Typography>
				<Button
					type="button"
					sx={{
						minWidth: { xs: '16px', sm: '24px' },
						height: { xs: '16px', sm: '24px' },
						fontSize: '24px',
						color: titleTextColor,
					}}
					onClick={handleHistoryModeClose}
				>
					<span>&times;</span>
				</Button>
			</Box>

			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
					height: '80vh',
				}}
			>
				{isHistoryLoading ? (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							width: '100%',
							height: '100%',
						}}
					>
						<Oval
							height={24}
							width={24}
							color="#fff"
							wrapperStyle={{}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="#91B6FF"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					</Box>
				) : (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '16px',
							padding: '0 0 0 12px',
							maxHeight: '85%',
							overflow: 'auto',
						}}
					>
						{ticketHistory.map((change, index) => (
							<HistoryItem
								key={index}
								historyItem={change}
								isFirstChange={index === ticketHistory.length - 1}
							/>
						))}
					</Box>
				)}

				<Button
					sx={{
						display: { xs: 'none', md: 'flex' },
						marginTop: '24px',
						height: '44px',
						borderRadius: '8px',
						textTransform: 'none',
						fontSize: '16px',
						fontWeight: '700',
						gap: '8px',
					}}
					variant="outlined"
					color="primary"
					onClick={handleOpenReportAnIssue}
				>
					<Warning />
					{commonTranslate('buttons.reportAnIssue')}
				</Button>
			</Box>
		</Box>
	);
};
